import { AlertTriangle, ChevronLeft, ChevronRight, Download, Loader2, Maximize2, Menu, Minimize2, RefreshCw, Upload } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import toast from 'react-hot-toast';
import type { MatterDetail } from '@/types';
import { useSidePanel } from '@/components/contexts/SidePanelContext';
import { useShellHeaderBelow } from '@/components/contexts/ShellHeaderContext';
import { CitedDocumentsList } from '@/components/shared/CitedDocumentsList';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DropdownMenuItemWithTooltip } from '@/components/ui/DropdownMenuItemWithTooltip';
import { Progress } from "@/components/ui/progress";
import { useMatterMemoryContext } from '@/components/contexts/MatterMemoryContext';
import { type EcaProgressParams } from '@/hooks/useMatterMemory';
import { useCaseOverviewContent } from '@/hooks/useCaseOverviewContent';
import { stripCitationsFromContent } from '@/utils/citation-utils';
import { exportMarkdownAsDocx } from '@/utils/docx-export';
import { htmlToMarkdown, markdownToHtml } from '@/utils/markdown-conversion';
import { useEarlyCaseAssessmentContent } from '@/hooks/useEarlyCaseAssessmentContent';
import { useFolders } from '@/hooks/useFolders';
import { useFolderDropZone } from '@/hooks/useFolderDropZone';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useSuperAdminCheck } from '@/hooks/useSuperAdminCheck';
import { apiErrorMessage, backendClient } from "@/lib/api-client";
import { findClientDocumentsRoot } from '@/lib/eca-client-documents';
import { overviewDocumentSuggestions, useMatterLabels } from '@/lib/useMatterLabels';
import { filterFilesWithToast } from '@/lib/fileFilterUtils';
import { FileUpload, type FileUploadHandles } from '@/components/shared/FileUpload';
import { Loading } from '../shared';
import { OverviewSectionCard } from '@/components/shared/OverviewSectionCard';
import { MatterMemorySection } from './MatterMemorySection';
import { EarlyCaseAssessmentActionBar } from './EarlyCaseAssessmentActionBar';
import {
  DEFAULT_GAUGING_MODEL,
  DEFAULT_GAUGING_VERBOSITY,
  ModelVerbosityPicker,
  type GaugingModel,
  type GaugingVerbosity,
} from './ModelVerbosityPicker';

interface MatterMemoryProps {
  matter: MatterDetail;
  className?: string;
  stage?: 'overview' | 'early';
  /** Stage switcher tabs, rendered bottom-left of the shell header. */
  stageTabs?: ReactNode;
}

const MATTER_MEMORY_TITLE_REGEX = /^\s*#\s*Matter Memory:[^\n]*\n?/im;
const SECTION_HEADING_REGEX = /(^|\n)(##\s+[^\n]+)\n*/g;
const DENSE_BULLET_LIST_REGEX = /\n- \*\*/g;
const EXCESS_NEWLINES_REGEX = /\n{3,}/g;

function formatOverviewForExport(content: string): string {
  return content
    .replace(MATTER_MEMORY_TITLE_REGEX, '')
    .replace(SECTION_HEADING_REGEX, '$1$2\n\n&nbsp;\n\n')
    .replace(DENSE_BULLET_LIST_REGEX, '\n\n- **')
    .replace(EXCESS_NEWLINES_REGEX, '\n\n')
    .trim();
}

interface MatterMemoryHeaderActionsProps {
  isEarlyStage: boolean;
  earlyCaseAssessmentActionBar: React.JSX.Element | null;
  overviewActionMenu: React.JSX.Element | null;
  generationProgress: React.JSX.Element | null;
}

const MatterMemoryHeaderActions = ({
  isEarlyStage,
  earlyCaseAssessmentActionBar,
  overviewActionMenu,
  generationProgress,
}: MatterMemoryHeaderActionsProps) => {
  if (isEarlyStage) {
    return (
      <div className="flex flex-col gap-3 md:flex-row md:items-baseline md:justify-between md:gap-4 lg:gap-6">
        <div className="w-full md:ml-auto md:w-auto md:pr-2 lg:pr-3">
          {earlyCaseAssessmentActionBar}
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-start justify-end gap-4">
      <div className="flex flex-col items-end justify-end gap-2 min-w-[200px] self-stretch">
        <div className="flex items-center justify-end gap-2">
          {overviewActionMenu}
        </div>
        {generationProgress}
      </div>
    </div>
  );
};
const MatterMemory = ({ matter, className, stage = 'overview', stageTabs }: MatterMemoryProps) => {
  const { isSuperAdmin } = useSuperAdminCheck();
  const label = useMatterLabels();
  const slug = matter.guid;
  const isEarlyStage = stage === 'early';
  const [ecaSavedSelection, setEcaSavedSelection] = useState<string[]>([]);
  const ecaProgressParams = useMemo((): EcaProgressParams => {
    if (!isEarlyStage) {
      return null;
    }
    const fileReferenceGuids: string[] = [];
    const folderGuids: string[] = [];
    for (const id of ecaSavedSelection) {
      if (id.startsWith('file:')) {
        fileReferenceGuids.push(id.slice('file:'.length));
      } else if (id.startsWith('folder:')) {
        folderGuids.push(id.slice('folder:'.length));
      }
    }
    return { fileReferenceGuids, folderGuids };
  }, [isEarlyStage, ecaSavedSelection]);

  const {
    markdown: earlyCaseAssessmentMarkdown,
    progress: earlyCaseAssessmentProgress,
    contributingDocumentsCount: earlyCaseAssessmentContributingDocumentsCount,
    ecaExplicitFileReferenceGuids,
    ecaExplicitFolderGuids,
    ecaDroppedPersistedPickCount,
    ecaOverDocumentLimit,
    ecaDocumentLimit,
    ecaEffectiveDocumentCount,
    freshnessStatus: earlyCaseAssessmentFreshnessStatus,
    loadingSummary: loadingEarlyCaseAssessment,
    summaryError: earlyCaseAssessmentError,
    isGenerating: isGeneratingEarlyCaseAssessment,
    triggerEarlyCaseGenerate,
    fetchEarlyCaseAssessment,
  } = useEarlyCaseAssessmentContent(isEarlyStage ? matter.guid : undefined, ecaProgressParams);

  // Re-hydrate `ecaSavedSelection` whenever the backend's persisted picks change.
  const lastHydratedEcaPicksRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isEarlyStage) return;
    if (ecaExplicitFileReferenceGuids === null && ecaExplicitFolderGuids === null) return;
    const key = JSON.stringify([
      ecaExplicitFileReferenceGuids ?? null,
      ecaExplicitFolderGuids ?? null,
    ]);
    if (lastHydratedEcaPicksRef.current === key) return;
    const hydrated = [
      ...(ecaExplicitFileReferenceGuids ?? []).map((guid) => `file:${guid}`),
      ...(ecaExplicitFolderGuids ?? []).map((guid) => `folder:${guid}`),
    ];
    setEcaSavedSelection(hydrated);
    lastHydratedEcaPicksRef.current = key;
  }, [isEarlyStage, ecaExplicitFileReferenceGuids, ecaExplicitFolderGuids]);

  // Toast when saved picks have gone stale.
  const lastDroppedToastKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isEarlyStage) return;
    if (!ecaDroppedPersistedPickCount || ecaDroppedPersistedPickCount <= 0) return;
    // Dedupe per (matter, count); re-fires if the count changes.
    const key = `${matter.guid}:${ecaDroppedPersistedPickCount}`;
    if (lastDroppedToastKeyRef.current === key) return;
    lastDroppedToastKeyRef.current = key;
    const noun = ecaDroppedPersistedPickCount === 1 ? 'item is' : 'items are';
    toast(
      `${ecaDroppedPersistedPickCount} previously-selected ${noun} no longer available; we've used the rest for this analysis.`,
      { icon: '\u26a0\ufe0f', duration: 8000 },
    );
  }, [isEarlyStage, ecaDroppedPersistedPickCount, matter.guid]);

  const {
    matterMemory,
    progress,
    loadingSummary,
    summaryError,
    isGenerating,
    fetchMatterMemory,
    markGenerationPending,
  } = useMatterMemoryContext();
  const resolvedMarkdown = isEarlyStage ? earlyCaseAssessmentMarkdown : matterMemory;
  const activeProgress = isEarlyStage ? earlyCaseAssessmentProgress : progress;
  const activeLoadingSummary = isEarlyStage ? loadingEarlyCaseAssessment : loadingSummary;
  const activeSummaryError = isEarlyStage ? earlyCaseAssessmentError : summaryError;
  const activeIsGenerating = isEarlyStage ? isGeneratingEarlyCaseAssessment : isGenerating;

  // ECA empty state opens / accepts drops into Client Documents directly.
  const { folders: ecaFolders } = useFolders(isEarlyStage ? slug : '');
  const clientDocsFolder = useMemo(
    () => (isEarlyStage ? findClientDocumentsRoot(ecaFolders) : undefined),
    [isEarlyStage, ecaFolders],
  );
  const clientDocsFolderId = clientDocsFolder?.folder_id ?? null;
  const ecaUploadRef = useRef<FileUploadHandles>(null);
  const isEcaEmptyState =
    isEarlyStage &&
    !activeIsGenerating &&
    !activeLoadingSummary &&
    !activeSummaryError &&
    !resolvedMarkdown;
  const isEcaUploadEnabled = isEcaEmptyState && clientDocsFolderId != null;

  const handleEcaFilesDropped = useCallback(
    (files: File[]) => {
      if (clientDocsFolderId == null) return;
      const validFiles = filterFilesWithToast(files);
      if (!validFiles) return;
      void ecaUploadRef.current?.openWithFiles(validFiles, String(clientDocsFolderId));
    },
    [clientDocsFolderId],
  );

  const { isDraggingOver: isEcaEmptyDraggingOver, dragHandlers: ecaEmptyDragHandlers } =
    useFolderDropZone({
      folderId: clientDocsFolderId ?? 0,
      folderName: clientDocsFolder?.name ?? 'Client Documents',
      onFilesDropped: handleEcaFilesDropped,
      disabled: !isEcaUploadEnabled,
    });

  const openEcaUploadDialog = useCallback(() => {
    if (!isEcaUploadEnabled) return;
    ecaUploadRef.current?.openDialog();
  }, [isEcaUploadEnabled]);

  const handleEcaUploadComplete = useCallback(() => {
    void fetchEarlyCaseAssessment();
  }, [fetchEarlyCaseAssessment]);

  // Component-specific state (editing, UI, etc.)
  const [editingSectionId, setEditingSectionId] = useState<string | null>(null);
  const [editedContent, setEditedContent] = useState<Record<string, string>>({});
  const [savingSummary, setSavingSummary] = useState(false);
  const [showEditWarning, setShowEditWarning] = useState<string | null>(null);
  const [showSaveConfirmation, setShowSaveConfirmation] = useState<string | null>(null);
  const [showRegenerateConfirmation, setShowRegenerateConfirmation] = useState(false);
  const [gaugingModel, setGaugingModel] = useState<GaugingModel>(DEFAULT_GAUGING_MODEL);
  const [gaugingVerbosity, setGaugingVerbosity] = useState<GaugingVerbosity>(DEFAULT_GAUGING_VERBOSITY);
  const [copiedSection, setCopiedSection] = useState<string | null>(null);
  const [regeneratingSummary, setRegeneratingSummary] = useState(false);
  const [isExportingDocx, setIsExportingDocx] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
  const [showAdminTab, setShowAdminTab] = useState(true);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const {
    documents,
    citedDocuments,
    sections,
    renderSectionHtml,
  } = useCaseOverviewContent({
    markdownContent: resolvedMarkdown,
    slug,
    scrollRef: scrollContainerRef,
  });

  const { isOpen: isRightPanelOpen } = useSidePanel();
  // Tailwind's `xl` breakpoint (1280px).
  const isLargeScreen = useMediaQuery('(min-width: 1280px)');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(!isLargeScreen || isRightPanelOpen);

  // Auto-collapse sidebar when right panel opens
  useEffect(() => {
    if (isRightPanelOpen && !sidebarCollapsed) {
      setSidebarCollapsed(true);
    }
  }, [isRightPanelOpen]);

  // Expand the sections panel automatically at/above the `xl` breakpoint, and
  // collapse it below. The right panel keeps precedence: while it's open the
  // sections panel stays collapsed regardless of width.
  useEffect(() => {
    if (isRightPanelOpen) return;
    setSidebarCollapsed(!isLargeScreen);
  }, [isLargeScreen, isRightPanelOpen]);

  // Citation click handler is now global in AppShell - no need for local handler

  // Initialize all sections as expanded when sections change
  useEffect(() => {
    if (sections.length > 0) {
      // Start with all sections expanded (empty set)
      setCollapsedSections(new Set());
      // Set first section as active initially
      if (!activeSectionId) {
        setActiveSectionId(sections[0].id);
      }
    }
  }, [sections]);

  // Load the admin tab visibility preference from localStorage
  useEffect(() => {
    const stored = localStorage.getItem('showAdminTab');
    if (stored !== null) {
      setShowAdminTab(stored === 'true');
    }
  }, []);

  // Track active section based on scroll position
  useEffect(() => {
    if (sections.length === 0) return;

    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const handleScroll = () => {
      // Find the section closest to the top of the viewport
      // We use a trigger point slightly below the top (200px) to account for the header
      const triggerPoint = 200;

      let closestSection: string | null = null;
      let closestDistance = Infinity;

      // Create array of all sections including Citations
      const allSections = [...sections];
      if (citedDocuments.size > 0) {
        allSections.push({ id: 'section-sources', title: 'Citations', content: '', level: 2 });
      }

      // Check if we're near the bottom of the scroll container
      const scrolledToBottom = scrollContainer.scrollTop + scrollContainer.clientHeight >= scrollContainer.scrollHeight - 50;

      // If scrolled to bottom, highlight the last section
      if (scrolledToBottom) {
        closestSection = allSections[allSections.length - 1].id;
      } else {
        // Check each section to find which one is closest to the trigger point
        allSections.forEach((section) => {
          const element = document.getElementById(section.id);
          if (element) {
            const rect = element.getBoundingClientRect();

            // If section's top is above the trigger point, calculate distance
            if (rect.top <= triggerPoint) {
              const distance = triggerPoint - rect.top;
              if (distance < closestDistance) {
                closestDistance = distance;
                closestSection = section.id;
              }
            }
          }
        });

        // If no section is above trigger point, use the first section
        if (!closestSection) {
          closestSection = sections[0].id;
        }
      }

      // Update active section if it changed
      if (closestSection && closestSection !== activeSectionId) {
        setActiveSectionId(closestSection);
      }
    };

    // Initial check
    handleScroll();

    // Listen to scroll on the actual scroll container
    scrollContainer.addEventListener('scroll', handleScroll);
    return () => scrollContainer.removeEventListener('scroll', handleScroll);
  }, [sections, activeSectionId, citedDocuments]);

  const showEditWarningDialog = (sectionId: string) => {
    setShowEditWarning(sectionId);
  };

  const confirmEditSection = (sectionId: string) => {
    const section = sections.find(s => s.id === sectionId);
    if (!section) return;

    // Convert Markdown to HTML for Quill editor
    const htmlContent = markdownToHtml(section.content);
    setEditedContent(prev => ({ ...prev, [sectionId]: htmlContent }));
    setEditingSectionId(sectionId);
    setShowEditWarning(null);
  };

  const cancelEditingSection = () => {
    if (editingSectionId) {
      setEditedContent(prev => {
        const newContent = { ...prev };
        delete newContent[editingSectionId];
        return newContent;
      });
    }
    setEditingSectionId(null);
  };

  const showSaveConfirmationDialog = (sectionId: string) => {
    setShowSaveConfirmation(sectionId);
  };

  const reconstructMarkdown = (sectionId: string, newContent: string): string => {
    // Find the section being edited
    const sectionIndex = sections.findIndex(s => s.id === sectionId);
    if (sectionIndex === -1) return matterMemory || '';

    // Extract the top-level heading if it exists
    const lines = (matterMemory || '').split('\n');
    let topLevelHeading = '';
    const firstLine = lines[0];
    const topHeadingMatch = firstLine.match(/^#\s+(.+)$/);
    if (topHeadingMatch) {
      topLevelHeading = firstLine + '\n\n';
    }

    // Extract the Citations section (everything after ## Citations heading)
    let citationsSection = '';
    const citationsIndex = lines.findIndex(line => line.match(/^##\s+Citations$/i));
    if (citationsIndex !== -1) {
      citationsSection = '\n\n' + lines.slice(citationsIndex).join('\n');
    }

    // Reconstruct the full markdown
    let reconstructed = topLevelHeading;

    sections.forEach((section, index) => {
      // Add the heading
      const headingPrefix = '#'.repeat(section.level);
      reconstructed += `${headingPrefix} ${section.title}\n\n`;

      // Add the content (edited if it's the current section)
      if (index === sectionIndex) {
        reconstructed += newContent.trim() + '\n\n';
      } else {
        reconstructed += section.content.trim() + '\n\n';
      }
    });

    // Append the Citations section if it existed
    reconstructed += citationsSection;

    return reconstructed.trim();
  };

  const confirmSaveSection = async (sectionId: string) => {
    if (isEarlyStage) {
      toast.error('Early case assessment content is not persisted yet.');
      setShowSaveConfirmation(null);
      return;
    }

    setShowSaveConfirmation(null);
    setSavingSummary(true);
    try {
      // Convert HTML back to Markdown
      const htmlFromEditor = editedContent[sectionId] || '';
      const markdownContent = htmlToMarkdown(htmlFromEditor);

      // Reconstruct full markdown with the edited section
      const fullMarkdown = reconstructMarkdown(sectionId, markdownContent);

      await backendClient.patch(`/matter-memory/${matter.guid}`, {
        summary: fullMarkdown
      });

      // Refresh matter memory from server
      await fetchMatterMemory();

      setEditingSectionId(null);
      setEditedContent(prev => {
        const newContent = { ...prev };
        delete newContent[sectionId];
        return newContent;
      });
      toast.success("Overview section updated successfully");
    } catch (error: any) {
      console.error("Error saving summary:", error);
      toast.error(apiErrorMessage(error) || "Failed to save overview");
    } finally {
      setSavingSummary(false);
    }
  };

  const copySectionContent = async (sectionId: string) => {
    const section = sections.find(s => s.id === sectionId);
    if (!section) return;

    try {
      // Copy the markdown content including the heading
      const headingPrefix = '#'.repeat(section.level);
      const fullContent = `${headingPrefix} ${section.title}\n\n${section.content}`;

      // Strip sources from content before copying
      const cleanContent = stripCitationsFromContent(fullContent);

      await navigator.clipboard.writeText(cleanContent);
      setCopiedSection(sectionId);

      // Reset the copied state after 2 seconds
      setTimeout(() => {
        setCopiedSection(null);
      }, 2000);
    } catch (error) {
      console.error("Error copying to clipboard:", error);
      toast.error("Failed to copy section");
    }
  };

  const showGaugingPicker = Boolean(isSuperAdmin && showAdminTab);

  const regenerateMatterMemory = async () => {
    if (isEarlyStage) {
      toast.error('Regeneration is only available for the case overview.');
      return;
    }

    setShowRegenerateConfirmation(false);
    setRegeneratingSummary(true);
    try {
      // Everyone uses the same tenant-scoped regenerate route (tenant resolved
      // from auth). Super admins additionally forward one-off model/verbosity
      // gauging overrides as query params, which the backend honors only for
      // super admins (via require_super_admin).
      const response = await backendClient.post(
        `/matter-memory/${matter.guid}/regenerate`,
        null,
        showGaugingPicker
          ? { params: { model: gaugingModel, verbosity: gaugingVerbosity } }
          : undefined,
      );

      if (response.data.status === 'no_documents') {
        toast.error("No documents available to generate overview");
      } else if (response.data.status === 'already_processing') {
        toast("Case overview generation is already in progress.");
        await fetchMatterMemory();
      } else {
        toast.success("Case overview regeneration started.");
        markGenerationPending();
      }
    } catch (error: unknown) {
      console.error("Error regenerating case overview:", error);
      toast.error(apiErrorMessage(error) || "Failed to regenerate case overview");
    } finally {
      setRegeneratingSummary(false);
    }
  };

  const exportOverviewAsDocx = async () => {
    if (!resolvedMarkdown) {
      toast.error("Overview content is not ready to export");
      return;
    }

    setIsExportingDocx(true);
    try {
      const exportMarkdown = isEarlyStage
        ? resolvedMarkdown
        : formatOverviewForExport(resolvedMarkdown);
      const matterIdentifier = typeof slug === 'string' ? slug : matter.guid;
      const filePrefix = isEarlyStage ? 'early-case-assessment' : 'case-overview';
      await exportMarkdownAsDocx(
        matter.guid,
        exportMarkdown,
        true,
        `${filePrefix}-${matterIdentifier}-${Date.now()}.docx`,
      );
    } catch (error: any) {
      console.error("Error exporting overview as DOCX:", error);
      toast.error(error?.message || "Failed to export overview as DOCX");
    } finally {
      setIsExportingDocx(false);
    }
  };

  const toggleSectionCollapse = (sectionId: string) => {
    setCollapsedSections(prev => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  };

  const expandAll = () => {
    setCollapsedSections(new Set());
  };

  const collapseAll = () => {
    const allSectionIds = new Set(sections.map(s => s.id));
    setCollapsedSections(allSectionIds);
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    const container = scrollContainerRef.current;

    if (element && container) {
      // Expand the section if it's collapsed first
      if (collapsedSections.has(sectionId)) {
        toggleSectionCollapse(sectionId);
      }

      // Get element position relative to the scroll container
      const elementRect = element.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      const relativeTop = elementRect.top - containerRect.top + container.scrollTop;
      const offsetPosition = relativeTop - 20; // Reduced offset so section is more visible for active highlight

      container.scrollTo({
        top: offsetPosition,
        behavior: 'auto'
      });
    }
  };

  const hasOverviewContent = Boolean(resolvedMarkdown && sections.length > 0);
  const showInitialLoading = activeLoadingSummary && !resolvedMarkdown;
  const showSummaryError = Boolean(activeSummaryError);
  const shouldShowGenerationProgress = activeIsGenerating && (!activeProgress || activeProgress.total_eligible_documents === 0 || activeProgress.processed_documents < activeProgress.total_eligible_documents);
  const allSectionsForSidebar = [
    ...sections,
    ...(citedDocuments.size > 0 ? [{ id: 'section-sources', title: 'Citations', content: '', level: 2 }] : []),
  ];
  const sidebarWidthClass = sidebarCollapsed ? 'w-12' : 'w-64';
  const sidebarHeaderPaddingClass = sidebarCollapsed ? 'px-2 pt-4 pb-3' : 'p-4 pb-3';
  const sidebarHeaderAlignmentClass = sidebarCollapsed ? 'justify-center' : '';
  const sidebarContentPaddingClass = sidebarCollapsed ? 'p-2' : 'p-4';
  const sidebarToggleTitle = sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar';
  const sidebarToggleIcon = sidebarCollapsed
    ? <ChevronRight className="h-4 w-4 text-slate-600" />
    : <ChevronLeft className="h-4 w-4 text-slate-600" />;

  const renderCollapsedSidebarItem = (
    section: { id: string; title: string },
    index: number,
    isActive: boolean
  ) => {
    const buttonClass = isActive
      ? 'w-full flex items-center justify-center p-2 rounded-lg transition-colors bg-slate-700 text-white'
      : 'w-full flex items-center justify-center p-2 rounded-lg transition-colors hover:bg-primary-50';
    const numberClass = isActive ? 'text-xs font-semibold text-white' : 'text-xs font-semibold text-primary-700';

    return (
      <button
        key={section.id}
        onClick={() => scrollToSection(section.id)}
        className={buttonClass}
        title={section.title}
      >
        <span className={numberClass}>{index + 1}</span>
      </button>
    );
  };

  const renderExpandedSidebarItem = (
    section: { id: string; title: string; content: string },
    index: number,
    isActive: boolean
  ) => {
    const displayTitle = index === 0 ? 'Key Facts' : section.title;
    const wordCount = section.content.trim().split(/\s+/).length;
    const readTime = Math.max(1, Math.ceil(wordCount / 200));
    const buttonClass = isActive
      ? 'w-full flex items-start gap-2 p-2 rounded-lg text-left transition-colors group bg-slate-100 hover:bg-slate-200 border border-slate-300'
      : 'w-full flex items-start gap-2 p-2 rounded-lg text-left transition-colors group hover:bg-primary-50';
    const badgeClass = isActive
      ? 'shrink-0 w-6 h-6 rounded-sm flex items-center justify-center bg-slate-700 text-white'
      : 'shrink-0 w-6 h-6 rounded-sm flex items-center justify-center bg-primary-100 group-hover:bg-primary-200';
    const numberClass = isActive ? 'text-xs font-semibold text-white' : 'text-xs font-semibold text-primary-700';
    const readTimeClass = isActive ? 'text-xs text-slate-600' : 'text-xs text-slate-500';

    return (
      <button
        key={section.id}
        onClick={() => scrollToSection(section.id)}
        className={buttonClass}
      >
        <div className={badgeClass}>
          <span className={numberClass}>{index + 1}</span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate text-primary" title={displayTitle}>{displayTitle}</p>
          <p className={readTimeClass}>
            {readTime} min read
          </p>
        </div>
      </button>
    );
  };

  const sidebarSectionItems = allSectionsForSidebar.map((section, index) => {
    const isActive = activeSectionId === section.id;

    if (sidebarCollapsed) {
      return renderCollapsedSidebarItem(section, index, isActive);
    }

    return renderExpandedSidebarItem(section, index, isActive);
  });

  const sidebarFooterActions = sidebarCollapsed ? (
    <div className="flex flex-col gap-2">
      <button
        onClick={expandAll}
        className="w-full p-1 rounded-lg hover:bg-primary-50 transition-colors flex items-center justify-center"
        title="Expand all sections"
      >
        <Maximize2 className="h-4 w-4 text-slate-600" />
      </button>
      <button
        onClick={collapseAll}
        className="w-full p-1 rounded-lg hover:bg-primary-50 transition-colors flex items-center justify-center"
        title="Collapse all sections"
      >
        <Minimize2 className="h-4 w-4 text-slate-600" />
      </button>
    </div>
  ) : (
    <div className="flex gap-2">
      <button
        onClick={expandAll}
        className="flex-1 text-xs text-slate-600 hover:text-primary px-3 py-2 rounded-lg hover:bg-primary-50 transition-colors font-medium"
        title="Expand all sections"
      >
        Expand All
      </button>
      <button
        onClick={collapseAll}
        className="flex-1 text-xs text-slate-600 hover:text-primary px-3 py-2 rounded-lg hover:bg-primary-50 transition-colors font-medium"
        title="Collapse all sections"
      >
        Collapse All
      </button>
    </div>
  );

  const canRegenerateOverview =
    matter.role === 'Owner' || (isSuperAdmin && showAdminTab);
  const isOverviewRegenerationDisabled =
    regeneratingSummary ||
    activeIsGenerating ||
    activeProgress?.total_eligible_documents === 0;
  const overviewDownloadDisabledReason = isExportingDocx
    ? 'The case overview is currently being exported.'
    : !resolvedMarkdown
      ? 'Download is available after the case overview has been generated.'
      : null;
  const overviewRegenerationDisabledReason = regeneratingSummary || activeIsGenerating
    ? 'Case overview generation is already in progress.'
    : activeProgress?.total_eligible_documents === 0
      ? 'Upload eligible case documents before regenerating the case overview.'
      : null;

  const generationProgress = shouldShowGenerationProgress ? (
    <div className="w-full">
      <Progress value={activeProgress?.percentage_complete || 0} className="w-full h-1.5 mb-0.5" />
      <div className="flex flex-row justify-between gap-1">
        <span className="text-xs text-slate-500 leading-tight flex items-center gap-1">
          <Loader2 className="h-3 w-3 animate-spin" />
          {!activeProgress || activeProgress.total_eligible_documents === 0
            ? label('analysing-documents')
            : `${activeProgress.processed_documents} of ${activeProgress.total_eligible_documents} processed`
          }
        </span>
        <span className="text-xs text-slate-500">
          {!activeProgress || activeProgress.total_eligible_documents === 0
            ? ''
            : `${Math.round(activeProgress.percentage_complete)}%`
          }
        </span>
      </div>
    </div>
  ) : null;

  const overviewActionMenu = !isEarlyStage ? (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type="button" variant="ghost" size="sm" className="h-8 w-8 p-0">
          <span className="sr-only">More actions</span>
          <Menu className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="space-y-1">
        <DropdownMenuItemWithTooltip
          onClick={exportOverviewAsDocx}
          disabled={isExportingDocx || !resolvedMarkdown}
          disabledReason={overviewDownloadDisabledReason}
          className="flex items-center gap-2 px-3 py-2"
        >
          <Download className="h-4 w-4" />
          {isExportingDocx ? 'Exporting\u2026' : 'Download'}
        </DropdownMenuItemWithTooltip>
        {canRegenerateOverview && (
          <DropdownMenuItemWithTooltip
            onClick={() => setShowRegenerateConfirmation(true)}
            disabled={isOverviewRegenerationDisabled}
            disabledReason={overviewRegenerationDisabledReason}
            className="flex items-center gap-2 px-3 py-2"
          >
            <RefreshCw className={`h-4 w-4 ${regeneratingSummary ? 'animate-spin' : ''}`} />
            {regeneratingSummary ? 'Regenerating\u2026' : 'Regenerate'}
          </DropdownMenuItemWithTooltip>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  ) : null;

  const earlyCaseAssessmentActionBar = isEarlyStage ? (
    <EarlyCaseAssessmentActionBar
      slug={slug}
      savedSelection={ecaSavedSelection}
      onSavedSelectionChange={setEcaSavedSelection}
      triggerGenerate={triggerEarlyCaseGenerate}
      onDownload={exportOverviewAsDocx}
      isDownloading={isExportingDocx}
      isDownloadDisabled={isExportingDocx || !resolvedMarkdown}
      isGenerating={isGeneratingEarlyCaseAssessment}
      contributingDocumentCount={earlyCaseAssessmentContributingDocumentsCount}
      freshnessStatus={earlyCaseAssessmentFreshnessStatus}
      overDocumentLimit={ecaOverDocumentLimit}
      documentLimit={ecaDocumentLimit}
      effectiveDocumentCount={ecaEffectiveDocumentCount}
      isSuperAdmin={showGaugingPicker}
    />
  ) : null;

  // Surface forum- and region-appropriate document types in the info / empty states.
  const overviewDocs = overviewDocumentSuggestions(label, matter.forum);

  // Render the stage tabs + header actions into the global shell header so the
  // bottom border aligns edge-to-edge with the nav sidebar (the in-page scroll
  // container lives inside the shell header's scrollbar-gutter inset, so it can't
  // reach the full content width the shell header spans). Tabs sit bottom-left,
  // actions on the right.
  //
  // Intentionally not memoized: the embedded action elements are recreated
  // inline each render and the row legitimately re-renders with live generation
  // progress, so a useMemo here would recompute (and re-dispatch) every render anyway.
  useShellHeaderBelow(
    <div className="flex items-end justify-between gap-4">
      {stageTabs && <div className="shrink-0">{stageTabs}</div>}
      <div className="flex-1 min-w-0 pb-2">
        <MatterMemoryHeaderActions
          isEarlyStage={isEarlyStage}
          earlyCaseAssessmentActionBar={earlyCaseAssessmentActionBar}
          overviewActionMenu={overviewActionMenu}
          generationProgress={generationProgress}
        />
      </div>
    </div>
  );

  const loadingStateContent = (
    <div className="px-6 py-6">
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <Loading text='Loading overview' />
        </div>
      </div>
    </div>
  );

  const errorStateContent = (
    <div className="px-6 py-6">
      <div className="max-w-4xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <p className="text-sm text-red-800">{activeSummaryError}</p>
        </div>
      </div>
    </div>
  );

  const overviewContent = (
    <div>
      <div className="px-2 space-y-4">
        {sections.map((section, index) => {
          const isCollapsed = collapsedSections.has(section.id);
          const isEditing = editingSectionId === section.id;

          return (
            <MatterMemorySection
              key={section.id}
              section={section}
              index={index}
              isCollapsed={isCollapsed}
              isEditing={isEditing}
              canEdit={!isEarlyStage}
              copiedSection={copiedSection}
              editedHtml={editedContent[section.id] || ''}
              savingSummary={savingSummary}
              regeneratingSummary={regeneratingSummary}
              isGenerating={activeIsGenerating}
              onToggleCollapse={() => !isEditing && toggleSectionCollapse(section.id)}
              onCancelEdit={cancelEditingSection}
              onSave={() => showSaveConfirmationDialog(section.id)}
              onCopy={() => copySectionContent(section.id)}
              onShowEditWarning={() => showEditWarningDialog(section.id)}
              onEditHtmlChange={(content) => setEditedContent(prev => ({ ...prev, [section.id]: content }))}
              renderSectionHtml={renderSectionHtml}
            />
          );
        })}

        {citedDocuments.size > 0 && (
          <OverviewSectionCard
            id="section-sources"
            number={sections.length + 1}
            title="Citations"
            collapsed={collapsedSections.has('section-sources')}
            onToggle={() => toggleSectionCollapse('section-sources')}
            indicatorStyle="up-down"
          >
            <CitedDocumentsList
              citedDocuments={citedDocuments}
              documents={documents}
              compact={false}
              slug={slug}
            />
          </OverviewSectionCard>
        )}
      </div>
    </div>
  );

  const ecaEmptyStateBoxClass = [
    'rounded-xl p-12 text-center transition-colors',
    isEcaUploadEnabled
      ? isEcaEmptyDraggingOver
        ? 'border-2 border-dashed border-crimson bg-crimson/5 cursor-pointer'
        : 'border-2 border-dashed border-slate-300 bg-slate-50 hover:bg-slate-100 cursor-pointer'
      : 'border border-slate-200 bg-slate-50',
  ].join(' ');

  const emptyStateBoxProps = isEcaUploadEnabled
    ? {
        ...ecaEmptyDragHandlers,
        onClick: openEcaUploadDialog,
        role: 'button' as const,
        tabIndex: 0,
        onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openEcaUploadDialog();
          }
        },
        'aria-label': 'Upload documents to Client Documents to generate an early case assessment',
      }
    : {};

  const emptyStateBoxClass = isEcaUploadEnabled
    ? ecaEmptyStateBoxClass
    : 'bg-slate-50 border border-slate-200 rounded-xl p-12 text-center';

  const emptyStateContent = (
    <div className="px-6 py-6">
      <div className="max-w-4xl mx-auto">
        <div className={emptyStateBoxClass} {...emptyStateBoxProps}>
          <div className="max-w-md mx-auto">
            {activeIsGenerating ? (
              <>
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Loading />
                </div>
                <h4 className="text-lg font-semibold text-primary mb-2">
                  {isEarlyStage ? 'Generating Early Case Assessment' : 'Generating Overview'}
                </h4>
                <p className="text-sm text-slate-600">
                  {isEarlyStage
                    ? label('overview-generating-eca')
                    : label('overview-generating-docs', overviewDocs)}
                </p>
              </>
            ) : isEcaUploadEnabled ? (
              <>
                <div
                  className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
                    isEcaEmptyDraggingOver ? 'bg-crimson/10' : 'bg-slate-100'
                  }`}
                >
                  <Upload
                    className={`w-8 h-8 ${
                      isEcaEmptyDraggingOver ? 'text-crimson' : 'text-slate-400'
                    }`}
                  />
                </div>
                <h4 className="text-lg font-semibold text-primary mb-2">
                  No Early Case Assessment Available
                </h4>
                <p className="text-sm text-slate-600">
                  {isEcaEmptyDraggingOver
                    ? 'Drop to upload to Client Documents.'
                    : 'Drop documents here or click to upload to Client Documents. Crimson will generate an early case assessment automatically.'}
                </p>
              </>
            ) : (
              <>
                <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h4 className="text-lg font-semibold text-primary mb-2">
                  {isEarlyStage ? 'No Early Case Assessment Available' : 'No Overview Available'}
                </h4>
                <p className="text-sm text-slate-600">
                  {isEarlyStage
                    ? label('eca-empty-generate-hint')
                    : label('overview-empty-hint', overviewDocs)}
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  let mainContent = emptyStateContent;
  if (showInitialLoading) {
    mainContent = loadingStateContent;
  } else if (showSummaryError) {
    mainContent = errorStateContent;
  } else if (hasOverviewContent) {
    mainContent = overviewContent;
  }

  return (
    <div className={`h-full pt-2 ${className || ''}`}>
      {isEarlyStage && clientDocsFolderId != null && (
        <div className="hidden" aria-hidden>
          <FileUpload
            ref={ecaUploadRef}
            matterGuid={matter.guid}
            showAsDialog
            documentType={String(clientDocsFolderId)}
            onUploadComplete={handleEcaUploadComplete}
          />
        </div>
      )}
      <div className="h-full bg-primary-50 py-3 px-6 relative flex flex-col overflow-hidden">
        {/* Content Area with Sidebar and Main Content */}
        <div className="flex-1 relative flex overflow-hidden">
        {/* Sections Navigation Sidebar */}
        {hasOverviewContent && (
          <div
            className={`shrink-0 transition-all duration-300 ease-in-out mr-1 ${sidebarWidthClass}`}>
            <div className="h-full bg-white rounded-xl overflow-hidden flex flex-col border border-slate-200">
            {/* Header with collapse toggle */}
            <div className={`shrink-0 border-b border-slate-200 ${sidebarHeaderPaddingClass}`}>
              <div className={`flex items-center gap-2 ${sidebarHeaderAlignmentClass}`}>
                {/* Collapse/Expand Toggle - Top Left */}
                <Button
                  variant="ghost"
                  onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                  className="p-1.5 rounded-lg hover:bg-primary-50 transition-colors"
                  title={sidebarToggleTitle}
                >
                  {sidebarToggleIcon}
                </Button>

                {!sidebarCollapsed && (
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Sections</span>
                )}
              </div>
            </div>

            {/* Scrollable section list */}
            <div className="flex-1 overflow-y-auto">
              <div className={`${sidebarContentPaddingClass} space-y-2`}>
                {sidebarSectionItems}
              </div>
            </div>

            {/* Footer with Expand/Collapse buttons */}
            <div className="shrink-0 border-t border-slate-200 p-3">
              {sidebarFooterActions}
            </div>
          </div>
          </div>
        )}

        {/* Main content area */}
        <div className="flex-1 bg-white rounded-xl overflow-hidden flex flex-col">
          <div
            ref={scrollContainerRef}
            className="flex-1 min-h-0 relative overflow-y-auto main-content-scroll"
          >
            {mainContent}
          </div>
        </div>
        </div>
      </div>

      {/* Edit Warning Dialog */}
      <AlertDialog open={showEditWarning !== null} onOpenChange={(open) => !open && setShowEditWarning(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <div className="flex items-start gap-4">
              <div className="shrink-0 mt-1">
                <AlertTriangle className="h-6 w-6 text-amber-500" />
              </div>
              <div className="flex-1">
                <AlertDialogTitle>Edit Case Overview Section</AlertDialogTitle>
                <AlertDialogDescription className="mt-3 space-y-2">
                  <p>
                    Manually editing this section will override Crimson's understanding of this part of the case.
                    Please ensure your edits are accurate.
                  </p>
                  <p className="text-sm">
                    <strong>Note:</strong> When new documents are uploaded, the overview will be regenerated.
                    Your edits will be considered but are not guaranteed to remain.
                  </p>
                </AlertDialogDescription>
              </div>
            </div>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => showEditWarning && confirmEditSection(showEditWarning)}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              Continue Editing
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Regenerate Confirmation Dialog */}
      <AlertDialog
        open={showRegenerateConfirmation}
        onOpenChange={setShowRegenerateConfirmation}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Regenerate case overview?</AlertDialogTitle>
            <AlertDialogDescription>
              This rebuilds the case overview from scratch, which may consume a
              significant number of tokens. Your existing overview will be cleared
              when regeneration starts and replaced as generation progresses.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {showGaugingPicker && (
            <ModelVerbosityPicker
              model={gaugingModel}
              verbosity={gaugingVerbosity}
              onModelChange={setGaugingModel}
              onVerbosityChange={setGaugingVerbosity}
              disabled={regeneratingSummary}
            />
          )}
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={regenerateMatterMemory}
              className="bg-crimson text-white hover:bg-crimson/90"
            >
              Regenerate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Save Confirmation Dialog */}
      <AlertDialog open={showSaveConfirmation !== null} onOpenChange={(open) => !open && setShowSaveConfirmation(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Save changes?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to save these changes to this overview section? This will update Crimson's understanding of the case.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => showSaveConfirmation && confirmSaveSection(showSaveConfirmation)}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              Save changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default MatterMemory;
